import type { StealthRevealOnActionInput, StealthRevealOnActionVerdict } from '../types';

/**
 * STEALTH v2 §"attacking gives you away" (owner ruling 2026-09-04) — the TS twin of
 * ft_stealth_action_reveals_hidden() in includes/functions/janus/encounter/stealth_reveal_on_action.php.
 *
 * Making an ATTACK ROLL or CASTING A SPELL ends `hidden`. The one exemption is a caster carrying
 * `ranged_attacks_do_not_reveal_position` (Marksmen L5 "Quiet Breath"), which keeps them hidden
 * through RANGED ATTACKS only — never through melee, and never through a spell cast.
 *
 * Pure: it decides, it does not remove the status. The caller owns the side effect, exactly as the
 * PHP wrapper ft_stealth_reveal_after_action() does.
 *
 * The caller is responsible for normalizing the raw arrays into StealthRevealOnActionInput —
 * including folding the nested `attack_ability_arr` rider, whose roll_type/weapon_type stand in for
 * a host attack ability that carries neither. That mirrors how the PHP predicate walks
 * [$item_id_used_arr, $rider].
 */
export function stealthActionRevealsHidden(input: StealthRevealOnActionInput): StealthRevealOnActionVerdict {
    // ONLY A NEW ACTION REVEALS — engine re-entries (a persistent AOE ticking, a resumed cast)
    // replay the ORIGINAL ability's metadata, which the predicate cannot distinguish from a fresh
    // cast. The caller, which knows the frame kind, decides. Twin of the PHP early return.
    if (!input.isNewAction) {
        return { reveals: false, reason: 'not_a_new_action', ranged: false, exempt: false };
    }

    // A PASSIVE IS NEVER AN ACTION — passives self-dispatch through janus (triggers_after_combat_begins
    // and friends), and at least one live passive carries spell_circle, which would otherwise read as
    // a cast. Twin of the same early return in the PHP predicate.
    if (input.itemType === 'passive_ability') {
        return { reveals: false, reason: 'not_an_action', ranged: false, exempt: false };
    }

    const isAttackRoll = input.rollType === 'weapon_melee' || input.rollType === 'weapon_ranged'
        || input.rollType === 'spell_melee' || input.rollType === 'spell_ranged';
    const isAttackAbility = input.itemType === 'attack_ability' || input.itemType === 'opportunity_attack_ability';
    const isSpell = input.itemType === 'spell' || input.hasSpellLevel || input.hasSpellCircle;

    // HOSTILE ACTION (owner 2026-09-14): a direct damage roll, or a contest unless every declared contester
    // skill is a looking/hiding skill. No declared contester counts as hostile. Twin of PHP limb 4.
    const NON_HOSTILE_CONTESTERS = ['attention', 'inspection', 'sneak'];
    const isHostile = input.hostileRollType === 'damage'
        || (input.hostileRollType === 'contesting'
            && (input.contesterSkills.length === 0
                || input.contesterSkills.some(skill => !NON_HOSTILE_CONTESTERS.includes(skill))));

    if (!isAttackRoll && !isAttackAbility && !isSpell && !isHostile) {
        return { reveals: false, reason: 'not_an_action', ranged: false, exempt: false };
    }

    // The roll type settles ranged-ness when present; otherwise the equipped weapon's type does.
    // Unknown resolves to NOT ranged, so an unrecognised shape still reveals — the rule is "acting
    // gives you away", and the exemption is the narrow special case that must prove itself.
    const ranged = isAttackRoll
        ? (input.rollType === 'weapon_ranged' || input.rollType === 'spell_ranged')
        : input.weaponIsRanged;

    const reason: StealthRevealOnActionVerdict['reason'] =
        isAttackRoll ? 'attack_roll' : (isAttackAbility ? 'attack_ability' : (isSpell ? 'spell_cast' : 'hostile_action'));

    // Anything SPELL-SHAPED is excluded, by EITHER route: casting metadata (isSpell) or a spell
    // attack ROLL, which says "spell" on its own even with no metadata attached. Guarding on only
    // one of them let a bare spell_ranged attack stay exempt while the same attack carrying a
    // spell_circle did not — incoherent. (Both wrong versions caught in review 2026-09-04.)
    const isSpellAttack = input.rollType === 'spell_melee' || input.rollType === 'spell_ranged';
    if (ranged && reason !== 'hostile_action' && !isSpell && !isSpellAttack && input.casterExemptRangedReveal) {
        return { reveals: false, reason: 'exempt_ranged', ranged: true, exempt: true };
    }

    return { reveals: true, reason, ranged, exempt: false };
}
